import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import Sentry from 'config/sentry';

import {
  GetAlreadyListenedStoriesDocument,
  GetAlreadyListenedStoriesQuery,
} from 'graphql/generated/graphql';

import { buildGraphaelQueryOptions } from 'hooks/useGraphaelDefaults';
import { apolloClient } from 'hooks/withGraphQL';

import { refreshResults } from './algolia_filters';

export type UserAlreadyListenedStoriesState = {
  alreadyListenedStories: Array<string>;
};

type SelectorState = {
  userAlreadyListenedStories: UserAlreadyListenedStoriesState;
};

export const initialUserAlreadyListenedStoriesState: UserAlreadyListenedStoriesState = {
  alreadyListenedStories: [],
};

export const userAlreadyListenedStoriesSlice = createSlice({
  name: 'userAlreadyListenedStories',
  initialState: initialUserAlreadyListenedStoriesState,
  reducers: {
    setAlreadyListenedStories: (
      state,
      action: PayloadAction<Array<string>>,
    ) => {
      state.alreadyListenedStories = action.payload;
    },
    addAlreadyListenedStory: (state, action: PayloadAction<string>) => {
      state.alreadyListenedStories = [
        ...state.alreadyListenedStories,
        action.payload,
      ];
    },
    clearAlreadyListenedStories: (state) => {
      state.alreadyListenedStories =
        initialUserAlreadyListenedStoriesState.alreadyListenedStories;
    },
  },
});

export const fetchUserAlreadyListenedStories = () => async (
  dispatch,
  getState,
) => {
  try {
    const { auth, translations } = getState();

    const queryOptions = buildGraphaelQueryOptions(
      auth.isAuthenticated,
      auth.headers,
      translations.locale,
    );

    const { data } = await apolloClient.query<GetAlreadyListenedStoriesQuery>({
      query: GetAlreadyListenedStoriesDocument,
      ...queryOptions,
    });

    if (data) {
      dispatch(
        setAlreadyListenedStories(data.stories.stories.map((s) => s.id)),
      );
      dispatch(refreshResults());
    }
  } catch (error) {
    Sentry.captureException(error);
  }
};

export const {
  addAlreadyListenedStory,
  setAlreadyListenedStories,
  clearAlreadyListenedStories,
} = userAlreadyListenedStoriesSlice.actions;

export const getUserAlreadyListenedStories = (state: SelectorState) =>
  state.userAlreadyListenedStories.alreadyListenedStories;

export default userAlreadyListenedStoriesSlice.reducer;
